import { useState } from "react";
import { Link } from "react-router-dom";
import type { RadarRow, RadarSummary, Since } from "../api";
import { isWithheld } from "../api";
import type { Ability } from "../ability";
import { ClockBand } from "../components/ClockBand";
import { ContractPicker, useChosenContract } from "../components/ContractPicker";
import { Distribution } from "../components/Distribution";
import { EventDrawer } from "../components/EventDrawer";
import { SinceLast } from "../components/SinceLast";
import { Money, label } from "../components/Money";
import { Loading, Refused } from "../components/Shell";
import { SPENT, toneFor, type Tone } from "../components/tone";
import { Scrolls } from "../components/Table";
import { modifierLabel } from "../platform";
import { useApi } from "../useApi";
import { useKeyboardList } from "../useKeyboardList";

interface Payload { rows: RadarRow[]; summary: RadarSummary; since?: Since }

/**
 * What needs somebody, and how long they have.
 *
 * Ranked by what acting now would still change, and drawn the way a
 * control room draws a process: neutral everywhere, with colour spent
 * only where a person has to do something. A clock whose outcome has
 * already happened stays on the page — it is part of the record — but it
 * does not compete for attention with one that can still be answered.
 */
export function Radar({ ability }: { ability: Ability }) {
  const [contract, chooseContract] = useChosenContract();
  const { data, error, loading } = useApi<Payload>(
    `/radar${contract && contract !== "all" ? `?contract=${encodeURIComponent(contract)}` : ""}`);

  // Every hook runs before the early returns below. React counts them per
  // render, so a `useState` placed after `if (loading) return` is a
  // different number of hooks on the first render than on the second, and
  // the screen goes blank with "rendered more hooks than during the
  // previous render". Placement here is not style; it is the rule.
  const [filter, setFilter] = useState<Tone | null>(null);
  // Detail opens beside the list rather than replacing it: reading one row
  // should not cost the context you were reading it in.
  const [open, setOpen] = useState<string | null>(null);

  const rows = data?.rows ?? [];
  const shown = filter === null ? rows : rows.filter((r) => toneFor(r) === filter);
  const keys = useKeyboardList(shown.length, (i) => {
    const row = shown[i];
    if (row) setOpen(row.reference);
  }, open === null);

  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;

  // A contract column earns its place only when the list spans more than
  // one; otherwise it repeats the same reference on every row.
  const manyContracts = new Set(rows.map((r) => r.contract).filter(Boolean)).size > 1;
  const money = ability.can("view_financials");
  const anyActionable = data.rows.some((r) => ability.canActOn(r.responsible_party));
  const withheldCount = data.rows.filter((r) => isWithheld(r.exposure_gbp)).length;
  const nilCount = data.rows.filter((r) => r.exposure_gbp === null).length;

  const today = new Date().toISOString().slice(0, 10);
  const live = data.rows.filter((r) => !SPENT.has(r.state));
  const spent = data.rows.filter((r) => SPENT.has(r.state));
  // Running towards a dated automatic outcome: the only rows where doing
  // nothing today produces something irreversible.
  const running = live.filter((r) => toneFor(r) === "now").length;
  const late = live.filter((r) => toneFor(r) === "high").length;

  return (
    <>
      <div className="headrow">
        <h1>Deadline radar</h1>
        <ContractPicker chosen={contract} onChoose={chooseContract} />
      </div>
      <p className="standfirst">
        Ranked by what acting now would still change — not by which date is
        nearest. Colour marks the clocks running towards an outcome that
        happens on its own; being late is serious but not the same thing.
      </p>

      {/* Ordered by what can still be done about it, which is the only
          ordering that helps somebody deciding where to start. */}
      <SinceLast since={data.since} />

      <Distribution
        total={data.rows.length}
        bands={[
          { tone: "now", n: running, label: "running out on their own" },
          { tone: "high", n: late, label: "overdue, nothing automatic" },
          { tone: "open", n: live.length - running - late, label: "still inside the period" },
          { tone: "spent", n: spent.length, label: "outcome already taken" },
        ]}
        winnable={data.summary.value_at_risk_gbp}
        lost={data.summary.value_lost_gbp}
        filter={filter}
        onFilter={setFilter} />

      {!money && (
        <div className="note">
          <span className="h">Exposure figures are withheld</span>
          Your role does not include financial visibility.{" "}
          {withheldCount > 0 && (
            <>
              <span className="redacted" /> marks {withheldCount} figure
              {withheldCount === 1 ? "" : "s"} you may not see.{" "}
            </>
          )}
          {nilCount > 0 && (
            <>An em dash marks {nilCount} row{nilCount === 1 ? "" : "s"} carrying
            no figure at all.</>
          )}
        </div>
      )}

      <Scrolls>
      <table className="radar">
        <thead>
          <tr>
            {manyContracts && <th>Contract</th>}
            <th>Ref</th><th>Clock</th>
            <th>Where this clock stands</th>
            <th className="num">Deadline</th>
            <th className="num">At risk</th>
            {anyActionable && <th />}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => {
            const resolved = SPENT.has(r.state);
            const tone = toneFor(r);
            return (
              <tr key={r.clock_id} {...keys.rowProps(i)}>
                {/* Only when more than one is showing: a column repeating
                    the same reference nine times is not information. */}
                {manyContracts && <td className="mn withheld">{r.contract}</td>}
                <td className="mn">
                  <button type="button" className="linkish"
                    onClick={() => setOpen(r.reference)}>{r.reference}</button>
                </td>
                <td>
                  <span>{r.clock}</span>
                  <span className="k">clause {r.clause}</span>
                </td>
                <td><ClockBand row={r} today={today} /></td>
                <td className={`reading reading--${tone}`}>
                  <span className="reading__value">{reading(r, resolved)}</span>
                  <span className="reading__state">{label(r.state_label)}</span>
                  <span className="reading__date">{r.next_deadline ?? "—"}</span>
                </td>
                <td className="num mn">
                  {seenExposure(shown, r)
                    ? <span className="withheld alsoon" title={`Already counted above, on ${r.reference}`}>↳ also {r.reference}</span>
                    : <Money value={r.exposure_gbp} />}
                </td>
                {anyActionable && (
                  <td>
                    {resolved
                      ? <span className="withheld" title="The outcome has already taken effect. Nothing sent now changes it.">—</span>
                      : ability.canActOn(r.responsible_party)
                        ? <Link to="/board">Respond</Link>
                        : <span className="withheld" title={`${r.responsible_party.replace(/_/g, " ")} must act`}>—</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </Scrolls>
      <EventDrawer reference={open} onClose={() => setOpen(null)} />
      <p className="hint">
        {filter
          ? `Showing ${shown.length} of ${data.rows.length}. `
          : "Rights already lost stay on the list because they are part of the record, set quiet because nothing you do today moves them. "}
        <kbd>j</kbd><kbd>k</kbd> move · <kbd>↵</kbd> open ·{" "}
        <kbd>{modifierLabel()}</kbd><kbd>K</kbd> go to
        {filter && <> · <button className="linkish" onClick={() => setFilter(null)}>show all {data.rows.length}</button></>}
      </p>
    </>
  );
}

/** The figure a reader acts on, in words rather than a signed number. */
function reading(row: RadarRow, resolved: boolean): string {
  if (resolved) return "closed";
  if (row.days === null) return "—";
  if (row.days < 0) return `${Math.abs(row.days)}d over`;
  if (row.days === 0) return "today";
  return `${row.days}d left`;
}

/** Has this event's figure already been printed further up the table? */
function seenExposure(rows: RadarRow[], row: RadarRow): boolean {
  if (row.exposure_gbp === null) return false;
  const first = rows.find((r) => r.reference === row.reference && r.exposure_gbp !== null);
  return first !== undefined && first.clock_id !== row.clock_id;
}
